import { useMemo, useRef, useState } from "react";
import type { DramaCharacter, DramaRelation } from "@/mocks/dramas";

interface RelationshipGraphProps {
  characters: DramaCharacter[];
  relations: DramaRelation[];
}

const CANVAS_W = 720;
const CANVAS_H = 480;
const CENTER_X = CANVAS_W / 2;
const CENTER_Y = CANVAS_H / 2;
const RADIUS = 172;

interface Node {
  name: string;
  role?: string;
  x: number;
  y: number;
}

export default function RelationshipGraph({ characters, relations }: RelationshipGraphProps) {
  const [zoom, setZoom] = useState(() => (typeof window !== "undefined" && window.innerWidth < 768 ? 0.6 : 1));
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragging = useRef(false);
  const last = useRef({ x: 0, y: 0 });

  const nodes = useMemo<Node[]>(() => {
    const names: { name: string; role?: string }[] = [];
    const seen = new Set<string>();
    characters.forEach((c) => {
      if (!seen.has(c.name)) {
        seen.add(c.name);
        names.push({ name: c.name, role: c.role });
      }
    });
    relations.forEach((r) => {
      [r.source, r.target].forEach((n) => {
        if (!seen.has(n)) {
          seen.add(n);
          names.push({ name: n, role: "相关人物" });
        }
      });
    });
    const total = names.length || 1;
    return names.map((item, index) => {
      const angle = (-90 + (index * 360) / total) * (Math.PI / 180);
      return {
        ...item,
        x: CENTER_X + RADIUS * Math.cos(angle),
        y: CENTER_Y + RADIUS * Math.sin(angle),
      };
    });
  }, [characters, relations]);

  const edges = useMemo(() => {
    return relations
      .map((rel) => {
        const from = nodes.find((n) => n.name === rel.source);
        const to = nodes.find((n) => n.name === rel.target);
        if (!from || !to) return null;
        const dx = to.x - from.x;
        const dy = to.y - from.y;
        const length = Math.hypot(dx, dy);
        const angle = (Math.atan2(dy, dx) * 180) / Math.PI;
        return {
          key: `${rel.source}-${rel.target}-${rel.label}`,
          left: from.x,
          top: from.y,
          length,
          angle,
          label: rel.label,
          midX: (from.x + to.x) / 2,
          midY: (from.y + to.y) / 2,
        };
      })
      .filter((e): e is NonNullable<typeof e> => e !== null);
  }, [nodes, relations]);

  const onPointerDown = (e: React.PointerEvent) => {
    dragging.current = true;
    last.current = { x: e.clientX, y: e.clientY };
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragging.current) return;
    const dx = e.clientX - last.current.x;
    const dy = e.clientY - last.current.y;
    last.current = { x: e.clientX, y: e.clientY };
    setOffset((o) => ({ x: o.x + dx, y: o.y + dy }));
  };
  const onPointerUp = () => {
    dragging.current = false;
  };

  const clampZoom = (v: number) => Math.min(1.8, Math.max(0.5, Number(v.toFixed(2))));

  return (
    <div className="relative">
      <div
        className="relative overflow-hidden rounded-lg border border-background-200 bg-background-100 touch-none cursor-grab active:cursor-grabbing"
        style={{ height: 420 }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
        onWheel={(e) => {
          e.preventDefault();
          setZoom((z) => clampZoom(z - e.deltaY * 0.0015));
        }}
      >
        <div
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            width: CANVAS_W,
            height: CANVAS_H,
            marginLeft: -CANVAS_W / 2,
            marginTop: -CANVAS_H / 2,
            transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
            transformOrigin: "center center",
            transition: dragging.current ? "none" : "transform 0.15s ease-out",
          }}
        >
          {/* center caption */}
          <div
            className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary-600/10 text-primary-600 px-4 py-2 text-xs font-medium"
            style={{ left: CENTER_X, top: CENTER_Y }}
          >
            人物关系
          </div>

          {/* edges */}
          {edges.map((edge) => (
            <div key={edge.key}>
              <div
                className="absolute bg-accent-400/50"
                style={{
                  left: edge.left,
                  top: edge.top,
                  width: edge.length,
                  height: 2,
                  transform: `rotate(${edge.angle}deg)`,
                  transformOrigin: "0 0",
                }}
              />
              <span
                className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full bg-background-50 border border-background-300 px-2 py-0.5 text-[10px] text-foreground-600 whitespace-nowrap"
                style={{ left: edge.midX, top: edge.midY }}
              >
                {edge.label}
              </span>
            </div>
          ))}

          {/* nodes */}
          {nodes.map((node) => (
            <div
              key={node.name}
              className="absolute -translate-x-1/2 -translate-y-1/2 w-[104px] rounded-lg border border-primary-200 bg-background-50 px-2 py-2 text-center"
              style={{ left: node.x, top: node.y }}
            >
              <p className="font-heading text-sm font-semibold text-foreground-950 line-clamp-1">{node.name}</p>
              {node.role && (
                <p className="mt-0.5 text-[10px] leading-3 text-foreground-500 line-clamp-2">{node.role}</p>
              )}
            </div>
          ))}
        </div>

        {/* zoom controls */}
        <div className="absolute top-3 right-3 flex flex-col gap-1.5">
          <button
            type="button"
            aria-label="放大"
            onClick={() => setZoom((z) => clampZoom(z + 0.15))}
            className="w-8 h-8 flex items-center justify-center rounded-md bg-background-50 border border-background-300 text-foreground-700 hover:text-primary-600 transition-colors cursor-pointer"
          >
            <i className="ri-add-line" />
          </button>
          <button
            type="button"
            aria-label="缩小"
            onClick={() => setZoom((z) => clampZoom(z - 0.15))}
            className="w-8 h-8 flex items-center justify-center rounded-md bg-background-50 border border-background-300 text-foreground-700 hover:text-primary-600 transition-colors cursor-pointer"
          >
            <i className="ri-subtract-line" />
          </button>
          <button
            type="button"
            aria-label="重置视图"
            onClick={() => {
              setZoom(typeof window !== "undefined" && window.innerWidth < 768 ? 0.6 : 1);
              setOffset({ x: 0, y: 0 });
            }}
            className="w-8 h-8 flex items-center justify-center rounded-md bg-background-50 border border-background-300 text-foreground-700 hover:text-primary-600 transition-colors cursor-pointer"
          >
            <i className="ri-refresh-line" />
          </button>
        </div>

        <div className="absolute bottom-3 left-3 flex items-center gap-1.5 rounded-md bg-background-50/90 border border-background-200 px-2.5 py-1 text-[11px] text-foreground-500">
          <i className="ri-drag-move-2-line" />
          可拖动 / 滚轮缩放
        </div>
      </div>
    </div>
  );
}